import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Observable, timer } from 'rxjs';
import { take, takeUntil, filter, tap, withLatestFrom, debounceTime, skipUntil } from 'rxjs/operators';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import * as fromAuth from '../../store/auth/';
import * as fromRouting from '../../../core/store/routing';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import * as fromUserProfile from '../../../core/store/user-profile';
import { CookieService } from 'ngx-cookie-service';
import { LabelTranslationService } from 'src/app/modules/core/languages/label-translation.service';
import { CultureCodeWithLabel, DEFAULT_APP_CULTURE_CODE } from 'src/app/modules/core/languages/language.model';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  public destroy$ = new Subject<void>();
  public errorMessage$: Observable<string>;
  public isWorking$: Observable<boolean>;
  public isCompletingLogin$: Observable<boolean>;
  public showPassword$: Observable<boolean>;
  public username$: Observable<string>;
  languages: string[];
  showConfirm = false;
  isWorking = false;
  isWorkingTimeout = new Subject<void>();
  selectedCultureCode: string;
  loginHidden: boolean;

  constructor(
    private store: Store<any>,
    private routerStore: Store<fromRouting.State>,
    private fb: FormBuilder,
    private spinnerService: NgxSpinnerService,
    private notify: SystemNotificationsService,
    private translateService: TranslateService,
    private labelTranslationService: LabelTranslationService,
    private cookieService: CookieService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromAuth.loadAvailableLanguagesForLogin());

    if (!!!this.selectedCultureCode) {
      let language = window.localStorage && window.localStorage.getItem('language');
      if (!!!language) {
        this.selectedCultureCode = DEFAULT_APP_CULTURE_CODE;
      } else {
        this.selectedCultureCode = language;
      }
    }

    this.isWorkingTimeout.pipe(debounceTime(10000), takeUntil(this.destroy$)).subscribe(() => {
      if (this.isWorking) {
        this.store.dispatch(fromAuth.setIsWorking({ isWorking: false }));
      }
    });

    this.store
      .select(fromAuth.selectLanguages)
      .pipe(takeUntil(this.destroy$))
      .subscribe(languages => {
        this.languages = languages;

        if (!!this.languages && this.languages.length) {
          this.languageSelected();
        }
      });

    this.form = this.fb.group({
      username: ['', [Validators.required]],
      password: ['']
    });

    this.errorMessage$ = this.store.select(fromAuth.selectError);
    this.isWorking$ = this.store.select(fromAuth.selectIsWorking);
    this.showPassword$ = this.store.select(fromAuth.selectShowPassword);
    this.isCompletingLogin$ = this.store.select(fromAuth.selectIsCompletingLogin);
    this.username$ = this.store.select(fromAuth.selectUsername);

    this.routerStore
      .select(fromRouting.selectQueryParams)
      .pipe(withLatestFrom(this.store.select(fromAuth.selectIsCompletingLogin)), takeUntil(this.destroy$))
      .subscribe(([params, isCompletingLogin]) => {
        const code = params?.code;
        const state = params?.state;

        if (!!code && !!state) {
          this.loginHidden = true;

          const username = this.cookieService.get('auth-username');
          this.form.patchValue({ username });

          this.store.dispatch(fromAuth.setCompletingLogin({ isCompleting: true }));

          this.store.dispatch(fromAuth.processAuth0LoginResult({ code, state }));

          return;
        }

        if (isCompletingLogin) {
          // we need to reset the state of the login page
          this.store.dispatch(fromAuth.resetLogin());
        }
      });

    this.isWorking$
      .pipe(
        tap(isWorking => {
          isWorking ? this.spinnerService.show() : this.spinnerService.hide();
          this.isWorking = isWorking;
          if (this.isWorking) {
            this.isWorkingTimeout.next();
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.errorMessage$
      .pipe(
        skipUntil(timer(50)), // Skip any error already in the state when the login page loads
        filter(err => !!err),
        tap(value => {
          this.loginHidden = false;
          if (value == 'IP_ADDRESS_IS_RESRICTED') {
            this.store.dispatch(
              fromRouting.go({
                routeData: { path: ['login/ip-restricted'] }
              })
            );
          } else {
            value = `ERRORS.${value}`;
            this.translateService.get(value).subscribe(res => {
              this.notify.error(res);
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

      this.store.dispatch(fromAuth.hideAcceptancePage());
  }

  ngOnDestroy(): void {
    this.cookieService.delete('auth-username');
    this.destroy$.next();
    this.destroy$.complete();
  }

  loginStepEmail(): void {
    const { username } = this.form.value;

    const cultureCode = !!this.selectedCultureCode ? this.selectedCultureCode : DEFAULT_APP_CULTURE_CODE;

    this.cookieService.set('auth-username', username);

    this.store.dispatch(fromAuth.setIsWorking({ isWorking: true }));

    this.store.dispatch(fromAuth.loginStepEmail({ loginData: { username, cultureCode } }));
  }

  loginStepPassword(): void {
    let username: string;
    this.store
      .select(fromAuth.selectUsername)
      .pipe(take(1))
      .subscribe(n => (username = n));

    const { password } = this.form.value;

    const cultureCode = !!this.selectedCultureCode ? this.selectedCultureCode : DEFAULT_APP_CULTURE_CODE;

    this.cookieService.delete('auth-username');

    this.store.dispatch(fromAuth.setIsWorking({ isWorking: true }));

    this.store.dispatch(fromAuth.loginUserNamePassword({ loginData: { username, password, cultureCode } }));
  }

  backToEmail(): void {
    this.cookieService.delete('auth-username');
    this.form.patchValue({ password: null });
    this.store.dispatch(fromUserProfile.setConnection({ profileConnection: null }));
    this.store.dispatch(fromAuth.backToEmail());
  }

  sendPasswordLink(): void {
    let username: string;
    this.store
      .select(fromAuth.selectUsername)
      .pipe(take(1))
      .subscribe(n => (username = n));

    this.form.patchValue({ password: null });
    this.store.dispatch(fromAuth.sendPasswordResetLink({ username }));
  }

  forgotPassword() {
    this.showConfirm = true;
  }

  languageSelected(): void {
    this.store.dispatch(
      fromUserProfile.changeTranslationCulture({
        cultureCode: this.selectedCultureCode
      })
    );
    window.localStorage.setItem('language', this.selectedCultureCode);
  }

  confirmPasswordLinkResponse(response: boolean): void {
    this.showConfirm = false;

    if (response) {
      this.sendPasswordLink();
    }
  }

  getLanguageDisplay(lang: string) {
    if (!!lang) {
      return this.translateService.instant(`LANGUAGES.NATIVE.LANGUAGE-${lang}`);
    }
    return '';
  }
}
